import React, {
  useState,
  useEffect,
  useCallback,
  useMemo,
  useRef,
} from "react";
import {
  Table,
  OverlayTrigger,
  Tooltip,
  Spinner,
  Badge,
} from "react-bootstrap";
import FloatingTableModal from "./FloatingTableModal";
import { INVOICE_NOTES_INFO_HEADER } from "../modules/Invoice/ManageInvoice/actions/constants";
import {
  getInvoiceCaseNotes,
  getStandardChargeNotesDetails,
} from "../services/InvoiceAPI";
import { INVOICE_STATUS } from "./Constant";
import Loader from "./Loader";
import TruncateWithTooltip from "./TruncateWithTooltip";

interface DataTableProps {
  headers: {
    key: string;
    label: string;
    sortable?: boolean;
    style?: any;
    isNumeric?: boolean;
  }[];
  filterPayload?: any;
  activeTab: string;
  rows?: any[];
  onAction?: (action: string, row: any) => void;
  onSortAction?: (row: any, action?: string) => void;
  onRowSelect?: (selectedRows: any[]) => void;
  selectedRowIds?: string[];
  disabled?: boolean;
  isLogTable?: boolean;
  getInvoiceByClientQme?: (payload: any) => Promise<any[]>;
  getChildUser?: (payload: any) => Promise<any[]>;
  clientInvoiceData?: any[];
  resetTrigger?: boolean;
  isLoader?: boolean;
  isToDo?: boolean;
  loggedInUserid?: any;
}

interface RowLevel {
  id: string;
  level: number;
  isExpanded: boolean;
  data: any;
  children?: RowLevel[];
  isLoading?: boolean;
  isLeaf?: boolean;
  parentId?: string;
}

const statusClassMap = {
  [INVOICE_STATUS.INVOICE_GENERATED]: "Status_invoiceGenerated",
  [INVOICE_STATUS.INVOICE_AMENDED]: "status_Exception_nobillingconfig",
  [INVOICE_STATUS.EXCEPTION]: "status_Exception_nobillingconfig",
  [INVOICE_STATUS.EXCEPTION_NO_BILLING_CONFIG]:
    "status_Exception_nobillingconfig",
  [INVOICE_STATUS.EXCEPTION_MULTIPLE_BILLING_CONFIG]:
    "status_Exception_nobillingconfig",
  [INVOICE_STATUS.EXCEPTION_MISSING_PAGE_COUNT]:
    "status_Exception_nobillingconfig",
  [INVOICE_STATUS.EXCEPTION_MISSING_LINE_COUNT]:
    "status_Exception_nobillingconfig",
  [INVOICE_STATUS.EXCEPTION_INVALID_BILLING_CONFIG]:
    "status_Exception_Invalidbillingconfig",
  [INVOICE_STATUS.READY_TO_BILL]: "status_ReadytoBill",
  [INVOICE_STATUS.READY_TO_BILL_AMENDED]: "status_ReadytoBill",
  [INVOICE_STATUS.READY_TO_RE_BILL]: "status_Readytorebill",
  [INVOICE_STATUS.READY_TO_RE_BILL_AMENDED]: "status_Readytorebill",
  [INVOICE_STATUS.INVOICE_CANCELLED]: "status_Exception_nobillingconfig",
  [INVOICE_STATUS.CANCELLED]: "status_Exception_nobillingconfig",
  [INVOICE_STATUS.ON_HOLD]: "status_Exception_nobillingconfig",
};

const NestedExpandableTable: React.FC<DataTableProps> = ({
  headers,
  filterPayload,
  activeTab,
  rows = [],
  onAction,
  onSortAction,
  onRowSelect,
  selectedRowIds = [],
  disabled = false,
  isLogTable = false,
  getInvoiceByClientQme,
  getChildUser,
  clientInvoiceData = [],
  resetTrigger = false,
  isLoader = false,
  isToDo = false,
  loggedInUserid,
}) => {
  const [nestedRows, setNestedRows] = useState<RowLevel[]>([]);
  const [selectedRows, setSelectedRows] = useState<string[]>(
    selectedRowIds || [],
  );
  const [selectionUpdated, setSelectionUpdated] = useState(false);
  const [showModal, setShowModal] = useState(false);
  const [modalDetails, setModalDetails] = useState<any[]>([]);
  const [modalNotesData, setModalNotesData] = useState<any[]>([]);
  const [modalTitle, setModalTitle] = useState<string>("");
  const [modalPos, setModalPos] = useState<{
    top: number;
    left?: number;
    right?: number;
  } | null>(null);

  const isInitialMount = useRef(true);
  const prevClientInvoiceData = useRef<any[]>([]);
  const prevSelectedRowIds = useRef<string[]>(selectedRowIds || []);
  const nestedRowsRef = useRef<RowLevel[]>([]);
  const [loading, setLoading] = useState<boolean>(isLoader);

  useEffect(() => {
    nestedRowsRef.current = nestedRows;
  }, [nestedRows]);

  // const isRowEligibleForSelection = useCallback((rowData: any): boolean => {
  //   const eligibleStatuses = [
  //     INVOICE_STATUS.READY_TO_INVOICE,
  //     INVOICE_STATUS.READY_TO_REINVOICE,
  //     INVOICE_STATUS.EXCEPTION_NO_BILLING_CONFIG,
  //     INVOICE_STATUS.EXCEPTION_MISSING_PAGE_COUNT,
  //     INVOICE_STATUS.EXCEPTION_MISSING_LINE_COUNT,
  //     INVOICE_STATUS.INVOICED,
  //   ];
  //   return (
  //     rowData.invoiceStatus &&
  //     eligibleStatuses.includes(rowData.invoiceStatus.toString())
  //   );
  // }, []);

  const isRowEligibleForSelection = useCallback((rowData: any): boolean => {
    return !!rowData.invoiceStatus;
  }, []);

  const findRow = useCallback(
    (rows: RowLevel[], targetId: string): RowLevel | null => {
      for (const row of rows) {
        if (row.id === targetId) return row;
        if (row.children) {
          const found = findRow(row.children, targetId);
          if (found) return found;
        }
      }
      return null;
    },
    [],
  );

  const getAllEligibleLeafIds = useCallback(
    (row: RowLevel): string[] => {
      if (row.level === 2 || row.isLeaf) {
        return isRowEligibleForSelection(row.data) ? [row.id] : [];
      }

      let leafIds: string[] = [];
      if (row.children && row.children.length > 0) {
        row.children.forEach((child) => {
          leafIds = [...leafIds, ...getAllEligibleLeafIds(child)];
        });
      }
      return leafIds;
    },
    [isRowEligibleForSelection],
  );

  const toggleRowExpansion = useCallback(
    (rows: RowLevel[], rowId: string, isLoading?: boolean): RowLevel[] => {
      return rows.map((row) => {
        if (row.id === rowId) {
          return {
            ...row,
            isExpanded: isLoading ? row.isExpanded : !row.isExpanded,
            ...(isLoading !== undefined && { isLoading }),
          };
        }
        if (row.children) {
          return {
            ...row,
            children: toggleRowExpansion(row.children, rowId, isLoading),
          };
        }
        return row;
      });
    },
    [],
  );

  const updateRowChildren = useCallback(
    (rows: RowLevel[], parentId: string, children: RowLevel[]): RowLevel[] => {
      return rows.map((row) => {
        if (row.id === parentId) {
          return {
            ...row,
            children,
            isLoading: false,
            isExpanded: children?.length > 0,
          };
        }
        if (row.children) {
          return {
            ...row,
            children: updateRowChildren(row.children, parentId, children),
          };
        }
        return row;
      });
    },
    [],
  );

  const handleToggleExpand = useCallback(
    async (rowId: string, level: number) => {
      const row = findRow(nestedRows, rowId);
      if (!row) return;

      if (row.isExpanded) {
        setNestedRows((prev) => toggleRowExpansion(prev, rowId, false));
        return;
      }

      if (row.isLoading) return;

      const hasFetchedBefore = row.children !== undefined;

      // For level 0 and 1 rows that haven't been expanded yet, fetch children
      if ((level === 0 || level === 1) && !hasFetchedBefore) {
        setNestedRows((prev) => toggleRowExpansion(prev, rowId, true));
        try {
          let children: RowLevel[] = [];

          if (level === 0) {
            // Level 0 (Client) -> Fetch QME Doctors
            const clientId = parseInt(rowId.replace("client-", ""));
            const clientPayload = {
              ...filterPayload,
              clientId,
            };
            if (getInvoiceByClientQme) {
              const qmeDoctors = await getInvoiceByClientQme(clientPayload);

              children = qmeDoctors.map((qme) => ({
                id: `qme-${clientId}-${qme.qmeDoctorContactId}`,
                level: 1,
                isExpanded: false,
                isLoading: false,
                parentId: rowId,
                data: {
                  ...qme,
                  patientName: qme.qmeDoctorName,
                  noOfPages: qme.grandTotalNoOfPages,
                  noOfLines: qme.grandTotalNoOfLines,
                  amount: qme.grandTotalAmount,
                  totalAmount: qme.grandTotalAmount,
                  countOfCases: qme.countOfCases,
                  clientId: qme.clientId,
                  qmeDoctorContactId: qme.qmeDoctorContactId,
                },
              }));
            }
          } else if (level === 1) {
            // Level 1 (QME Doctor) -> Fetch User Details
            const parts = rowId.split("-");
            const clientId = parseInt(parts[1]);
            const qmeDoctorContactId =
              activeTab === "Role" ? parts[2] : parseInt(parts[2]);
            const paramNameMap: Record<string, string> = {
              Client: "qmeDoctorContactId",
              Role: "billingForUserRole",
              User: "userId",
            };

            const paramName = paramNameMap[activeTab] || "qmeDoctorContactId";
            const payloadCase = {
              ...filterPayload,
              clientId: clientId,
              [paramName]: isToDo ? loggedInUserid : qmeDoctorContactId,
            };

            if (getChildUser) {
              const userDetails = await getChildUser(payloadCase);

              children = userDetails.map((user, index) => ({
                id: `user-${clientId}-${qmeDoctorContactId}-${user.id || index}`,
                level: 2,
                isExpanded: false,
                isLoading: false,
                isLeaf: true,
                parentId: rowId,
                data: {
                  ...user,
                  patientName: user.patientName || "",
                  taskName: user.taskName || "",
                  noOfPages: user.noOfPages || "0",
                  noOfLines: user.noOfLines || "0",
                  rateType: user.rateType || "",
                  dateUploaded: user.dateUploaded || "",
                  rate: user.rate,
                  amount: user.amount,
                  invoiceStatus: user.invoiceStatus,
                  action: "View",
                  clientId: clientId,
                  qmeDoctorContactId: qmeDoctorContactId,
                },
              }));
            }
          }

          setNestedRows((prev) => updateRowChildren(prev, rowId, children));
        } catch (error) {
          console.error("Failed to fetch child data:", error);
          setNestedRows((prev) => toggleRowExpansion(prev, rowId, false));
        }
      } else {
        setNestedRows((prev) => toggleRowExpansion(prev, rowId));
      }
    },
    [
      nestedRows,
      findRow,
      toggleRowExpansion,
      updateRowChildren,
      getInvoiceByClientQme,
      getChildUser,
      filterPayload,
      activeTab,
    ],
  );

  const areAllEligibleChildrenSelected = useCallback(
    (row: RowLevel): boolean => {
      // Get all eligible leaf IDs recursively
      const getAllEligibleLeafIds = (currentRow: RowLevel): string[] => {
        let leafIds: string[] = [];

        const traverse = (r: RowLevel) => {
          if (r.level === 2 || r.isLeaf) {
            if (isRowEligibleForSelection(r.data)) {
              leafIds.push(r.id);
            }
          }
          if (r.children && r.children?.length > 0) {
            r.children.forEach((child) => traverse(child));
          }
        };

        traverse(currentRow);
        return leafIds;
      };

      const eligibleLeafIds = getAllEligibleLeafIds(row);

      // If no eligible leaves, return false
      if (eligibleLeafIds?.length === 0) return false;

      // Check if ALL eligible leaves are selected
      return eligibleLeafIds.every((id) => selectedRows.includes(id));
    },
    [selectedRows, isRowEligibleForSelection],
  );

  const isSomeChildrenSelected = useCallback(
    (row: RowLevel): boolean => {
      const eligibleLeafIds = getAllEligibleLeafIds(row);
      if (eligibleLeafIds?.length === 0) return false;

      const someSelected = eligibleLeafIds.some((id) =>
        selectedRows.includes(id),
      );
      const allSelected = eligibleLeafIds.every((id) =>
        selectedRows.includes(id),
      );

      return someSelected && !allSelected;
    },
    [selectedRows, getAllEligibleLeafIds],
  );

  // Main header checkbox - Select/Deselect ALL
  const handleSelectAll = useCallback(async () => {
    const expandAllRows = async (rows: RowLevel[]): Promise<RowLevel[]> => {
      const expandedRows = [...rows];

      for (let i = 0; i < expandedRows?.length; i++) {
        const row = expandedRows[i];

        // If this is a parent row (level 0 or 1) that's not expanded or has no children
        if (
          row.level < 2 &&
          (!row.isExpanded || !row.children || row.children?.length === 0)
        ) {
          expandedRows[i] = { ...row, isLoading: true };

          try {
            let children: RowLevel[] = [];

            if (row.level === 0) {
              // Level 0 (Client) -> Fetch QME Doctors
              const clientId = parseInt(row.id.replace("client-", ""));
              const clientPayload = {
                ...filterPayload,
                clientId,
              };
              if (getInvoiceByClientQme) {
                const qmeDoctors = await getInvoiceByClientQme(clientPayload);
                children = qmeDoctors.map((qme) => ({
                  id: `qme-${clientId}-${qme.qmeDoctorContactId}`,
                  level: 1,
                  isExpanded: false,
                  isLoading: false,
                  parentId: row.id,
                  data: {
                    ...qme,
                    patientName: qme.qmeDoctorName,
                    noOfPages: qme.grandTotalNoOfPages,
                    noOfLines: qme.grandTotalNoOfLines,
                    amount: qme.grandTotalAmount,
                    totalAmount: qme.grandTotalAmount,
                    countOfCases: qme.countOfCases,
                    clientId: qme.clientId,
                    qmeDoctorContactId: qme.qmeDoctorContactId,
                  },
                }));
              }
            } else if (row.level === 1) {
              // Level 1 (QME Doctor) -> Fetch User Details
              const parts = row.id.split("-");
              const clientId = parseInt(parts[1]);
              const qmeDoctorContactId =
                activeTab === "Role" ? parts[2] : parseInt(parts[2]);
              const paramNameMap: Record<string, string> = {
                Client: "qmeDoctorContactId",
                Role: "billingForUserRole",
                User: "userId",
              };

              const paramName = paramNameMap[activeTab] || "qmeDoctorContactId";
              const payloadCase = {
                ...filterPayload,
                clientId: clientId,
                [paramName]: isToDo ? loggedInUserid : qmeDoctorContactId,
              };

              if (getChildUser) {
                const userDetails = await getChildUser(payloadCase);
                children = userDetails.map((user, index) => ({
                  id: `user-${clientId}-${qmeDoctorContactId}-${user.id || index}`,
                  level: 2,
                  isExpanded: false,
                  isLoading: false,
                  isLeaf: true,
                  parentId: row.id,
                  data: {
                    ...user,
                    patientName: user.patientName || "",
                    taskName: user.taskName || "",
                    noOfPages: user.noOfPages || "0",
                    noOfLines: user.noOfLines || "0",
                    rateType: user.rateType || "",
                    dateUploaded: user.dateUploaded || "",
                    rate: user.rate,
                    amount: user.amount,
                    invoiceStatus: user.invoiceStatus,
                    action: "View",
                    clientId: clientId,
                    qmeDoctorContactId: qmeDoctorContactId,
                  },
                }));
              }
            }

            // Recursively expand children
            const expandedChildren = await expandAllRows(children);

            // Update the row with expanded children
            expandedRows[i] = {
              ...row,
              isExpanded: true,
              isLoading: false,
              children: expandedChildren,
            };
          } catch (error) {
            console.error("Failed to fetch child data:", error);
            expandedRows[i] = {
              ...row,
              isLoading: false,
              isExpanded: false,
            };
          }
        } else if (row.children && row.children.length > 0) {
          // Recursively expand children
          const expandedChildren = await expandAllRows(row.children);
          expandedRows[i] = {
            ...row,
            children: expandedChildren,
            isExpanded: true,
          };
        }
      }

      return expandedRows;
    };

    try {
      setNestedRows((prev) =>
        prev.map((row) => (row.level < 2 ? { ...row, isLoading: true } : row)),
      );

      // Expand all rows and get the fully expanded structure
      const fullyExpandedRows = await expandAllRows(nestedRows);
      setNestedRows(fullyExpandedRows);

      // Wait a moment for React to update the DOM
      await new Promise((resolve) => setTimeout(resolve, 50));

      // Now get all eligible leaf IDs from the fully expanded structure
      const getAllEligibleLeafIdsFromAllRows = (rows: RowLevel[]): string[] => {
        let eligibleIds: string[] = [];

        const traverse = (currentRow: RowLevel) => {
          if (currentRow.level === 2 || currentRow.isLeaf) {
            if (isRowEligibleForSelection(currentRow.data)) {
              eligibleIds.push(currentRow.id);
            }
          }
          if (currentRow.children && currentRow.children?.length > 0) {
            currentRow.children.forEach((child) => traverse(child));
          }
        };

        rows.forEach((row) => traverse(row));
        return eligibleIds;
      };

      const allEligibleLeafIds =
        getAllEligibleLeafIdsFromAllRows(fullyExpandedRows);

      if (allEligibleLeafIds?.length === 0) {
        setSelectedRows([]);
        setSelectionUpdated(true);
        return;
      }

      // Check if all eligible rows are currently selected
      const currentlyAllSelected = allEligibleLeafIds.every((id) =>
        selectedRows.includes(id),
      );

      let newSelectedRows: string[];

      if (currentlyAllSelected) {
        newSelectedRows = [];
      } else {
        newSelectedRows = [...allEligibleLeafIds];
      }

      setSelectedRows(newSelectedRows);
      setSelectionUpdated(true);
    } catch (error) {
      console.error("Failed to select all rows:", error);
    } finally {
      setNestedRows((prev) =>
        prev.map((row) => ({ ...row, isLoading: false })),
      );
    }
  }, [
    nestedRows,
    selectedRows,
    filterPayload,
    activeTab,
    getInvoiceByClientQme,
    getChildUser,
    isRowEligibleForSelection,
  ]);

  // Parent row checkbox (Level 0 & 1) - Select/Deselect all children
  const handleParentRowSelect = useCallback(
    async (row: RowLevel) => {
      if (row.level >= 2) return;

      const wasExpanded = row.isExpanded;

      // Function to load ALL children for a Level 0 row (Client) with Level 1 expanded
      const loadAllLevel0Children = async (
        clientRow: RowLevel,
      ): Promise<RowLevel[]> => {
        const clientId = parseInt(clientRow.id.replace("client-", ""));
        const clientPayload = {
          ...filterPayload,
          clientId,
        };

        if (!getInvoiceByClientQme) return [];

        const qmeDoctors = await getInvoiceByClientQme(clientPayload);
        // Load ALL Level 1 children (QME Doctors) with their Level 2 children
        const allLevel1Rows: RowLevel[] = [];

        for (const qme of qmeDoctors) {
          const level1Id = `qme-${clientId}-${qme.qmeDoctorContactId}`;

          // First, check if this Level 1 row already exists with loaded children
          const existingLevel1Row = findRow(nestedRows, level1Id);

          let level1Row: RowLevel;

          if (
            existingLevel1Row &&
            existingLevel1Row.children &&
            existingLevel1Row.children.length > 0
          ) {
            // Level 1 row already has children loaded, use it
            level1Row = { ...existingLevel1Row };
          } else {
            // Need to load Level 2 children for this QME doctor
            level1Row = {
              id: level1Id,
              level: 1,
              isExpanded: true,
              isLoading: false,
              parentId: clientRow.id,
              data: {
                ...qme,
                patientName: qme.qmeDoctorName,
                noOfPages: qme.grandTotalNoOfPages,
                noOfLines: qme.grandTotalNoOfLines,
                amount: qme.grandTotalAmount,
                totalAmount: qme.grandTotalAmount,
                countOfCases: qme.countOfCases,
                clientId: qme.clientId,
                qmeDoctorContactId: qme.qmeDoctorContactId,
              },
              children: [],
            };

            // Load ALL Level 2 children for this QME doctor
            const paramNameMap: Record<string, string> = {
              Client: "qmeDoctorContactId",
              Role: "billingForUserRole",
              User: "userId",
            };

            const paramName = paramNameMap[activeTab] || "qmeDoctorContactId";
            const payloadCase = {
              ...filterPayload,
              clientId: clientId,
              [paramName]: isToDo ? loggedInUserid : qme.qmeDoctorContactId,
            };

            if (getChildUser) {
              const userDetails = await getChildUser(payloadCase);
              level1Row.children = userDetails.map((user, index) => ({
                id: `user-${clientId}-${qme.qmeDoctorContactId}-${user.id || index}`,
                level: 2,
                isExpanded: false,
                isLoading: false,
                isLeaf: true,
                parentId: level1Id,
                data: {
                  ...user,
                  patientName: user.patientName || "",
                  taskName: user.taskName || "",
                  noOfPages: user.noOfPages || "0",
                  noOfLines: user.noOfLines || "0",
                  rateType: user.rateType || "",
                  dateUploaded: user.dateUploaded || "",
                  rate: user.rate,
                  amount: user.amount,
                  invoiceStatus: user.invoiceStatus,
                  action: "View",
                  clientId: clientId,
                  qmeDoctorContactId: qme.qmeDoctorContactId,
                },
              }));
            }
          }

          level1Row.isExpanded = true;
          allLevel1Rows.push(level1Row);
        }

        return allLevel1Rows;
      };

      // Function to load ALL children for a Level 1 row (QME Doctor)
      const loadAllLevel1Children = async (
        qmeRow: RowLevel,
      ): Promise<RowLevel[]> => {
        const parts = qmeRow.id.split("-");
        const clientId = parseInt(parts[1]);
        const qmeDoctorContactId =
          activeTab === "Role" ? parts[2] : parseInt(parts[2]);

        const paramNameMap: Record<string, string> = {
          Client: "qmeDoctorContactId",
          Role: "billingForUserRole",
          User: "userId",
        };

        const paramName = paramNameMap[activeTab] || "qmeDoctorContactId";
        const payloadCase = {
          ...filterPayload,
          clientId: clientId,
          [paramName]: isToDo ? loggedInUserid : qmeDoctorContactId,
        };

        if (!getChildUser) return [];
        const userDetails = await getChildUser(payloadCase);
        return userDetails.map((user, index) => ({
          id: `user-${clientId}-${qmeDoctorContactId}-${user.id || index}`,
          level: 2,
          isExpanded: false,
          isLoading: false,
          isLeaf: true,
          parentId: qmeRow.id,
          data: {
            ...user,
            patientName: user.patientName || "",
            taskName: user.taskName || "",
            noOfPages: user.noOfPages || "0",
            noOfLines: user.noOfLines || "0",
            rateType: user.rateType || "",
            dateUploaded: user.dateUploaded || "",
            rate: user.rate,
            amount: user.amount,
            invoiceStatus: user.invoiceStatus,
            action: "View",
            clientId: clientId,
            qmeDoctorContactId: qmeDoctorContactId,
          },
        }));
      };

      try {
        setNestedRows((prev) =>
          prev.map((r) =>
            r.id === row.id ? { ...r, isLoading: true, isExpanded: true } : r,
          ),
        );

        let allChildren: RowLevel[] = [];

        if (row.level === 0) {
          // Load ALL Level 1 rows with their Level 2 children
          allChildren = await loadAllLevel0Children(row);
        } else if (row.level === 1) {
          // Load ALL Level 2 children for this QME doctor
          allChildren = await loadAllLevel1Children(row);
        }

        // Update the state with all loaded children
        setNestedRows((prev) => {
          const withChildren = updateRowChildren(prev, row.id, allChildren);

          // For Level 0, expand it to show Level 1 rows
          return withChildren.map((r) =>
            r.id === row.id
              ? {
                  ...r,
                  isLoading: false,
                  isExpanded: row.level === 0 ? true : wasExpanded,
                }
              : r,
          );
        });

        // Wait for state to update
        await new Promise((resolve) => setTimeout(resolve, 50));

        // Get updated nested rows from ref
        const updatedNestedRows = nestedRowsRef.current;

        // Function to get ALL eligible leaf IDs from a row and its descendants
        const getAllEligibleLeafIdsFromRow = (
          targetRow: RowLevel,
        ): string[] => {
          const targetRowInState =
            findRow(updatedNestedRows, targetRow.id) || targetRow;
          let leafIds: string[] = [];

          const traverse = (currentRow: RowLevel) => {
            if (currentRow.level === 2 || currentRow.isLeaf) {
              if (isRowEligibleForSelection(currentRow.data)) {
                leafIds.push(currentRow.id);
              }
            }
            if (currentRow.children && currentRow.children.length > 0) {
              currentRow.children.forEach((child) => traverse(child));
            }
          };

          traverse(targetRowInState);
          return leafIds;
        };

        // Get ALL eligible leaf IDs
        const eligibleLeafIds = getAllEligibleLeafIdsFromRow(row);
        // Check if ALL these leaf IDs are already selected
        const areAllLeavesSelected =
          eligibleLeafIds?.length > 0 &&
          eligibleLeafIds.every((id) => selectedRows.includes(id));

        let newSelectedRows: string[];

        if (areAllLeavesSelected) {
          // Deselect all these leaves
          newSelectedRows = selectedRows.filter(
            (id) => !eligibleLeafIds.includes(id),
          );
        } else {
          // Select all eligible leaves
          newSelectedRows = [...new Set([...selectedRows, ...eligibleLeafIds])];
        }

        setSelectedRows(newSelectedRows);
        setSelectionUpdated(true);
      } catch (error) {
        setNestedRows((prev) =>
          prev.map((r) =>
            r.id === row.id
              ? { ...r, isLoading: false, isExpanded: wasExpanded }
              : r,
          ),
        );
      }
    },
    [
      nestedRows,
      selectedRows,
      findRow,
      updateRowChildren,
      filterPayload,
      activeTab,
      getInvoiceByClientQme,
      getChildUser,
      isRowEligibleForSelection,
    ],
  );

  // Leaf row checkbox (Level 2) - Individual selection
  const handleLeafRowSelect = useCallback(
    (rowId: string, rowData: any, level: number) => {
      if (level < 2) return;
      // Check if row is eligible for selection
      if (!isRowEligibleForSelection(rowData)) {
        return;
      }

      setSelectedRows((prevSelectedRows) => {
        let newSelectedRows: string[];

        if (prevSelectedRows.includes(rowId)) {
          newSelectedRows = prevSelectedRows.filter((id) => id !== rowId);
        } else {
          newSelectedRows = [...prevSelectedRows, rowId];
        }

        return newSelectedRows;
      });

      setSelectionUpdated(true);
    },
    [isRowEligibleForSelection],
  );

  const initializeNestedRowsWithFirstExpanded = useCallback(
    async (data: any[]): Promise<RowLevel[]> => {
      if (!data || data?.length === 0) return [];

      const initialNestedRows: RowLevel[] = [];

      for (let i = 0; i < data?.length; i++) {
        const client = data[i];
        const level0Id = `client-${client.clientId}`;
        const isFirstClient = i === 0;

        const level0Row: RowLevel = {
          id: level0Id,
          level: 0,
          isExpanded: isFirstClient,
          isLoading: isFirstClient,
          data: {
            ...client,
            patientName: client.clientName,
            noOfPages: client.grandTotalNoOfPages,
            noOfLines: client.grandTotalNoOfLines,
            amount: client.grandTotalAmount,
            totalAmount: client.grandTotalAmount,
            countOfQmeDoctors: client.countOfQmeDoctors,
          },
        };

        // If this is the first client, fetch and add its level 1 children
        if (isFirstClient && getInvoiceByClientQme) {
          try {
            const clientId = parseInt(client.clientId || client.id);
            const clientPayload = {
              ...filterPayload,
              clientId,
            };

            const qmeDoctors = await getInvoiceByClientQme(clientPayload);

            // Create level 1 rows and expand the first one
            level0Row.children = await Promise.all(
              qmeDoctors.map(async (qme, qmeIndex) => {
                const isFirstQme = qmeIndex === 0;
                const level1Id = `qme-${clientId}-${qme.qmeDoctorContactId}`;

                const level1Row: RowLevel = {
                  id: level1Id,
                  level: 1,
                  isExpanded: isFirstQme,
                  isLoading: isFirstQme,
                  parentId: level0Id,
                  data: {
                    ...qme,
                    patientName: qme.qmeDoctorName,
                    noOfPages: qme.grandTotalNoOfPages,
                    noOfLines: qme.grandTotalNoOfLines,
                    amount: qme.grandTotalAmount,
                    totalAmount: qme.grandTotalAmount,
                    countOfCases: qme.countOfCases,
                    clientId: qme.clientId,
                    qmeDoctorContactId: qme.qmeDoctorContactId,
                  },
                };

                // If this is the first level 1 row, fetch its level 2 children
                if (isFirstQme && getChildUser) {
                  try {
                    const paramNameMap: Record<string, string> = {
                      Client: "qmeDoctorContactId",
                      Role: "billingForUserRole",
                      User: "userId",
                    };

                    const paramName =
                      paramNameMap[activeTab] || "qmeDoctorContactId";
                    const payloadCase = {
                      ...filterPayload,
                      clientId: clientId,
                      [paramName]: isToDo
                        ? loggedInUserid
                        : qme.qmeDoctorContactId,
                    };

                    const userDetails = await getChildUser(payloadCase);

                    level1Row.children = userDetails.map((user, userIndex) => ({
                      id: `user-${clientId}-${qme.qmeDoctorContactId}-${user.id || userIndex}`,
                      level: 2,
                      isExpanded: false,
                      isLoading: false,
                      isLeaf: true,
                      parentId: level1Id,
                      data: {
                        ...user,
                        patientName: user.patientName || "",
                        taskName: user.taskName || "",
                        noOfPages: user.noOfPages || "0",
                        noOfLines: user.noOfLines || "0",
                        rateType: user.rateType || "",
                        dateUploaded: user.dateUploaded || "",
                        rate: user.rate,
                        amount: user.amount,
                        invoiceStatus: user.invoiceStatus,
                        action: "View",
                        clientId: clientId,
                        qmeDoctorContactId: qme.qmeDoctorContactId,
                      },
                    }));

                    level1Row.isLoading = false;
                  } catch (error) {
                    console.error(
                      "Failed to fetch level 2 data for first level 1 row:",
                      error,
                    );
                    level1Row.isLoading = false;
                    level1Row.isExpanded = false;
                  }
                }

                return level1Row;
              }),
            );

            level0Row.isLoading = false;
          } catch (error) {
            console.error(
              "Failed to fetch level 1 data for first client:",
              error,
            );
            level0Row.isLoading = false;
            level0Row.isExpanded = false;
          }
        }

        initialNestedRows.push(level0Row);
      }

      return initialNestedRows;
    },
    [filterPayload, activeTab, getInvoiceByClientQme, getChildUser],
  );

  useEffect(() => {
    const hasDataChanged =
      JSON.stringify(prevClientInvoiceData.current) !==
      JSON.stringify(clientInvoiceData);

    if (isInitialMount.current || hasDataChanged) {
      let initialNestedRows: RowLevel[] = [];

      if (clientInvoiceData && clientInvoiceData?.length > 0) {
        setLoading(true);
        initializeNestedRowsWithFirstExpanded(clientInvoiceData)
          .then((rows) => {
            setNestedRows(rows);
          })
          .catch((error) => {
            console.error("Failed to initialize nested rows:", error);
            const fallbackRows = clientInvoiceData.map((client) => ({
              id: `client-${client.clientId}`,
              level: 0,
              isExpanded: false,
              isLoading: false,
              data: {
                ...client,
                patientName: client.clientName,
                noOfPages: client.grandTotalNoOfPages,
                noOfLines: client.grandTotalNoOfLines,
                amount: client.grandTotalAmount,
                totalAmount: client.grandTotalAmount,
                countOfQmeDoctors: client.countOfQmeDoctors,
              },
            }));
            setNestedRows(fallbackRows);
          })
          .finally(() => {
            setLoading(false);
          });
      } else if (rows && rows?.length > 0) {
        const firstRowId = rows[0]?.id || `row-${Math.random()}`;
        initialNestedRows = rows.map((row, index) => ({
          id: row.id || `row-${Math.random()}`,
          level: 0,
          isExpanded: index === 0,
          isLoading: false,
          data: row,
        }));
        setNestedRows(initialNestedRows);
      }

      prevClientInvoiceData.current = clientInvoiceData;
    }

    isInitialMount.current = false;
  }, [clientInvoiceData, rows, initializeNestedRowsWithFirstExpanded]);

  // Sync external selectedRowIds
  useEffect(() => {
    const hasChanged =
      JSON.stringify(prevSelectedRowIds.current) !==
      JSON.stringify(selectedRowIds);

    if (hasChanged) {
      setSelectedRows(selectedRowIds || []);
      prevSelectedRowIds.current = selectedRowIds || [];
    }
  }, [selectedRowIds]);

  // Handle selection changes
  useEffect(() => {
    if (selectionUpdated && onRowSelect) {
      const selectedRowData = selectedRows
        .map((id) => {
          const row = findRow(nestedRows, id);
          return row ? row.data : null;
        })
        .filter(Boolean);

      onRowSelect(selectedRowData);
      setSelectionUpdated(false);
    }
  }, [selectionUpdated, selectedRows, nestedRows, onRowSelect, findRow]);

  const isAllSelected = useMemo(() => {
    if (nestedRows?.length === 0) return false;

    // Get all eligible leaf IDs
    const getAllEligibleLeafIdsFromAllRows = (rows: RowLevel[]): string[] => {
      let eligibleIds: string[] = [];

      const traverse = (currentRow: RowLevel) => {
        if (currentRow.level === 2 || currentRow.isLeaf) {
          if (isRowEligibleForSelection(currentRow.data)) {
            eligibleIds.push(currentRow.id);
          }
        }
        if (currentRow.children && currentRow.children.length > 0) {
          currentRow.children.forEach((child) => traverse(child));
        }
      };

      rows.forEach((row) => traverse(row));
      return eligibleIds;
    };

    const allEligibleLeafIds = getAllEligibleLeafIdsFromAllRows(nestedRows);

    if (allEligibleLeafIds.length === 0) return false;

    return allEligibleLeafIds.every((id) => selectedRows.includes(id));
  }, [nestedRows, selectedRows, isRowEligibleForSelection]);

  // Check if some (but not all) rows are selected
  const isSomeSelected = useMemo(() => {
    if (nestedRows?.length === 0) return false;

    const getAllEligibleLeafIdsFromAllRows = (rows: RowLevel[]): string[] => {
      let eligibleIds: string[] = [];

      const traverse = (currentRow: RowLevel) => {
        if (currentRow.level === 2 || currentRow.isLeaf) {
          if (isRowEligibleForSelection(currentRow.data)) {
            eligibleIds.push(currentRow.id);
          }
        }
        if (currentRow.children && currentRow.children.length > 0) {
          currentRow.children.forEach((child) => traverse(child));
        }
      };

      rows.forEach((row) => traverse(row));
      return eligibleIds;
    };

    const allEligibleLeafIds = getAllEligibleLeafIdsFromAllRows(nestedRows);

    if (allEligibleLeafIds.length === 0) return false;

    const someSelected = allEligibleLeafIds.some((id) =>
      selectedRows.includes(id),
    );
    const allSelected = allEligibleLeafIds.every((id) =>
      selectedRows.includes(id),
    );

    return someSelected && !allSelected;
  }, [nestedRows, selectedRows, isRowEligibleForSelection]);

  const handleOpenModal = async (invoice: any, e: React.MouseEvent) => {
    setModalTitle("Details");
    const baseDetails = [
      { label: "Case ID", value: invoice.caseId },
      { label: "Patient", value: invoice.patientName },
      { label: "Doctor", value: invoice.qmeDoctorName },
      { label: "Priority", value: invoice?.priorityDesc || "" },
    ];

    const conditionalDetails =
      invoice.taskName !== "Standard Charge"
        ? [
            {
              label: "Case Exam Type",
              value: invoice?.appointmentTypeName || "",
            },
            {
              label: "Appointment Date & Time",
              value: invoice?.dateOfAppointmentFormat,
            },
          ]
        : [];

    setModalDetails([...baseDetails, ...conditionalDetails]);

    const rect = (e.target as HTMLElement).getBoundingClientRect();
    const modalWidth = window.innerWidth * 0.5;
    const verticalOffset = 5;
    const horizontalOffset = 18;
    let position: { top: number; left?: number; right?: number };
    if (rect.left < window.innerWidth / 2) {
      position = {
        top: rect.bottom + verticalOffset,
        left: rect.left + horizontalOffset,
      };
    } else {
      position = {
        top: rect.bottom + verticalOffset,
        left: rect.right - modalWidth - horizontalOffset,
      };
    }

    setModalPos(position);
    setShowModal(true);
  };

  const handleNotesView = async (invoice: any, e: React.MouseEvent) => {
    setModalTitle("Notes");

    let notesDetails = [];

    if (invoice?.subjectQmeScheduleID) {
      notesDetails = await getInvoiceCaseNotes(
        invoice.subjectQmeScheduleID,
        invoice.invoiceLineItemId,
      );

      setModalNotesData(
        notesDetails?.[0]?.subjectQmeScheduleId ? notesDetails : [],
      );
    } else {
      notesDetails = await getStandardChargeNotesDetails(invoice?.caseInfoId);
      setModalNotesData(notesDetails?.[0]?.noteId ? notesDetails : []);
    }

    const rect = (e.target as HTMLElement).getBoundingClientRect();
    const modalWidth = window.innerWidth * 0.5;
    const verticalOffset = 5;
    const horizontalOffset = 18;
    let position: { top: number; left?: number; right?: number };
    if (rect.left < window.innerWidth / 2) {
      position = {
        top: rect.bottom + verticalOffset,
        left: rect.left + horizontalOffset,
      };
    } else {
      position = {
        top: rect.bottom + verticalOffset,
        left: rect.right - modalWidth - horizontalOffset,
      };
    }

    setModalPos(position);
    setShowModal(true);
  };

  const formatCurrency = useCallback((amount: number): string => {
    const number = new Intl.NumberFormat("en-US", {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    }).format(amount);

    return `$ ${number}`;
  }, []);

  const formatNumber = useCallback((num: number): string => {
    return new Intl.NumberFormat("en-US").format(num);
  }, []);

  const renderParentStats = useCallback(
    (row: RowLevel) => {
      if (row.level >= 2) return null;

      const rowData = row.data;

      const countColor = row.level === 0 ? "#c62828" : "#1565c0";
      const countBgColor = row.level === 0 ? "#E6E6E6" : "#F4F8FE";
      const countBorderColor = row.level === 0 ? "#ef9a9a" : "#90caf9";

      const pagesColor = row.level === 0 ? "#c62828" : "#1565c0";
      const pagesBgColor = row.level === 0 ? "#E6E6E6" : "#F4F8FE";
      const pagesBorderColor = row.level === 0 ? "#ef9a9a" : "#90caf9";

      const linesColor = row.level === 0 ? "#c62828" : "#1565c0";
      const linesBgColor = row.level === 0 ? "#E6E6E6" : "#F4F8FE";
      const linesBorderColor = row.level === 0 ? "#ef9a9a" : "#90caf9";

      return (
        <div style={{ display: "flex", gap: "8px", alignItems: "center" }}>
          <OverlayTrigger
            placement="top"
            overlay={<Tooltip>{`Cases: ${rowData.countOfCases || 0}`}</Tooltip>}
          >
            <span
              style={{
                marginLeft: "auto",
                alignItems: "center",
                gap: "4px",
                padding: "4px 10px",
                borderRadius: "12px",
                backgroundColor: countBgColor,
                border: `1px solid ${countBorderColor}`,
                fontSize: "12px",
                fontWeight: "500",
                minWidth: "90px",
                textAlign: "center",
                display: " inline-block",
                color: countColor,
              }}
            >
              <span className="material-icons" style={{ fontSize: "14px" }}>
                cases
              </span>
              <span className="countBlack"> {rowData.countOfCases || 0}</span>
            </span>
          </OverlayTrigger>

          <OverlayTrigger
            placement="top"
            overlay={
              <Tooltip>
                Total Pages:{" "}
                {rowData.noOfPages
                  ? formatNumber(parseInt(rowData.noOfPages))
                  : "0"}
              </Tooltip>
            }
          >
            <span
              style={{
                alignItems: "center",
                gap: "4px",
                padding: "4px 10px",
                borderRadius: "12px",
                backgroundColor: pagesBgColor,
                border: `1px solid ${pagesBorderColor}`,
                fontSize: "12px",
                fontWeight: "500",
                minWidth: "90px",
                textAlign: "center",
                display: " inline-block",
                color: pagesColor,
              }}
            >
              <span className="material-icons" style={{ fontSize: "14px" }}>
                auto_stories
              </span>
              <span className="countBlack">
                {" "}
                {rowData.noOfPages
                  ? formatNumber(parseInt(rowData.noOfPages))
                  : "0"}
              </span>
            </span>
          </OverlayTrigger>

          <OverlayTrigger
            placement="top"
            overlay={
              <Tooltip>
                Total Lines:{" "}
                {rowData.noOfLines
                  ? formatNumber(parseInt(rowData.noOfLines))
                  : "0"}
              </Tooltip>
            }
          >
            <span
              style={{
                alignItems: "center",
                gap: "4px",
                padding: "4px 10px",
                borderRadius: "12px",
                backgroundColor: linesBgColor,
                border: `1px solid ${linesBorderColor}`,
                fontSize: "12px",
                fontWeight: "500",
                minWidth: "90px",
                textAlign: "center",
                display: " inline-block",
                color: linesColor,
              }}
            >
              <span className="material-icons" style={{ fontSize: "14px" }}>
                notes
              </span>
              <span className="countBlack">
                {rowData.noOfLines
                  ? formatNumber(parseInt(rowData.noOfLines))
                  : "0"}
              </span>
            </span>
          </OverlayTrigger>

          <span
            style={{
              fontSize: "13px",
              fontWeight: "400",
              color: "#333",
              marginLeft: "8px",
              borderLeft: "1px solid #DFDADA",
              paddingLeft: "5px",
              minWidth: "145px",
              textAlign: "right",
            }}
          >
            <span style={{ float: "left" }}> Total</span> &nbsp;
            <span
              style={{
                fontSize: "13px",
                fontWeight: "600",
                color: "#333",
              }}
            >
              {rowData?.totalAmount || "0.00"}
            </span>
          </span>
        </div>
      );
    },
    [formatNumber],
  );

  const renderRows = useCallback(
    (rows: RowLevel[]): JSX.Element[] => {
      return rows.flatMap((row) => {
        const hasNoChildren =
          row.isExpanded && (!row.children || row.children?.length === 0);

        const isAllSelected = areAllEligibleChildrenSelected(row);
        const isSomeSelected = isSomeChildrenSelected(row);

        const rowElement = (
          <tr
            key={row.id}
            className={
              row.level === 0
                ? "invoice_gridStructure"
                : "invoice_gridStructureInner"
            }
          >
            {headers.map((header, index) => {
              if (header.key === "patientName" && row.level === 0) {
                return (
                  <td key={header.key} colSpan={5}>
                    <div
                      style={{
                        display: "flex",
                        alignItems: "center",
                        paddingLeft: "0px",
                      }}
                    >
                      <span
                        className="material-symbols-outlined"
                        style={{
                          cursor: row.isLoading ? "default" : "pointer",
                          color: row.isLoading ? "#ccc" : "#0098d7",
                          fontSize: "20px",
                          marginRight: "6px",
                        }}
                        onClick={() =>
                          !row.isLoading &&
                          handleToggleExpand(row.id, row.level)
                        }
                      >
                        {row.isLoading ? (
                          <Spinner animation="border" size="sm" />
                        ) : row.isExpanded ? (
                          "remove_circle"
                        ) : (
                          "add_circle"
                        )}
                      </span>
                      {!isToDo && (
                        <input
                          type="checkbox"
                          ref={(el) => {
                            if (el) {
                              el.indeterminate = isSomeSelected;
                            }
                          }}
                          checked={isAllSelected}
                          onChange={() => handleParentRowSelect(row)}
                          disabled={row.isLoading}
                          style={{
                            marginRight: "6px",
                            cursor: row.isLoading ? "default" : "pointer",
                          }}
                        />
                      )}
                      <OverlayTrigger
                        placement="auto"
                        delay={{ show: 200, hide: 10 }}
                        overlay={
                          row.data.patientName ? (
                            <Tooltip>{row.data.patientName}</Tooltip>
                          ) : (
                            <></>
                          )
                        }
                      >
                        <span
                          className="text-truncate"
                          style={{ maxWidth: "220px" }}
                        >
                          {row.isLoading ? "Loading..." : row.data.patientName}
                        </span>
                      </OverlayTrigger>

                      {row.data?.countOfQmeDoctors && !isToDo && (
                        <Badge className="ms-2 primary-badge">
                          {activeTab === "Client"
                            ? row.data.countOfQmeDoctors
                            : activeTab === "Role"
                              ? row.data.countOfUserRoles
                              : activeTab === "User"
                                ? row.data.countOfUsers
                                : 0}
                        </Badge>
                      )}
                    </div>
                  </td>
                );
              }

              if (header.key === "patientName" && row.level === 1 && !isToDo) {
                return (
                  <td key={header.key} colSpan={5}>
                    <div
                      style={{
                        display: "flex",
                        alignItems: "center",
                        paddingLeft: "25px",
                      }}
                    >
                      <span
                        className="material-symbols-outlined"
                        style={{
                          cursor: row.isLoading ? "default" : "pointer",
                          color: row.isLoading ? "#ccc" : "#0098d7",
                          fontSize: "20px",
                          marginRight: "6px",
                        }}
                        onClick={() =>
                          !row.isLoading &&
                          handleToggleExpand(row.id, row.level)
                        }
                      >
                        {row.isLoading ? (
                          <Spinner animation="border" size="sm" />
                        ) : row.isExpanded ? (
                          "remove_circle"
                        ) : (
                          "add_circle"
                        )}
                      </span>
                      {!isToDo && (
                        <input
                          type="checkbox"
                          ref={(el) => {
                            if (el) {
                              el.indeterminate = isSomeSelected;
                            }
                          }}
                          checked={isAllSelected}
                          onChange={() => handleParentRowSelect(row)}
                          disabled={row.isLoading}
                          style={{
                            marginRight: "6px",
                            cursor: row.isLoading ? "default" : "pointer",
                          }}
                        />
                      )}
                      <span>
                        {row.isLoading ? "Loading..." : row.data.patientName}
                      </span>
                    </div>
                  </td>
                );
              }

              if (header.key === "patientName" && row.level === 2) {
                const isEligible = isRowEligibleForSelection(row.data);
                return (
                  <td key={header.key} className="whitegurad">
                    <div
                      style={{
                        display: "flex",
                        alignItems: "flex-start",
                        paddingLeft: "55px",
                      }}
                    >
                      <input
                        type="checkbox"
                        checked={selectedRows.includes(row.id)}
                        onChange={() =>
                          handleLeafRowSelect(row.id, row.data, row.level)
                        }
                        disabled={!isEligible}
                        style={{
                          marginRight: "6px",
                          cursor: isEligible ? "pointer" : "not-allowed",
                          opacity: isEligible ? 1 : 0.5,
                          marginTop: "5px",
                        }}
                      />
                      <TruncateWithTooltip className="text-truncate truncatetwoline customDoc_line">
                        <span>{row.data.patientName}</span>
                      </TruncateWithTooltip>
                      <span
                        className="material-symbols-outlined ms-2"
                        style={{
                          cursor: "pointer",
                          color: "#f39c12",
                          fontSize: "18px",
                        }}
                        onClick={(e) => handleOpenModal(row.data, e)}
                      >
                        info
                      </span>
                    </div>
                  </td>
                );
              }

              if (
                (isToDo ? row.level < 1 : row.level < 2) &&
                header.key === "rate"
              ) {
                return (
                  <td key={header.key} colSpan={5}>
                    {renderParentStats(row)}
                  </td>
                );
              }

              if (
                row.level < 2 &&
                index > headers.findIndex((h) => h.key === "rate")
              ) {
                return null;
              }

              if (row.level === 2) {
                return (
                  <td
                    className="whitegurad"
                    key={header.key}
                    style={{
                      padding: header.isNumeric ? "8px 20px 8px 8px" : "8px",
                      textAlign: header.isNumeric
                        ? "center"
                        : header.key === "action"
                          ? "center"
                          : "left",
                    }}
                  >
                    {header.key === "action" && (
                      <div
                        className="actionSpace"
                        style={{
                          display: "flex",
                          justifyContent: "end",
                          gap: "0px",
                        }}
                      >
                        <span
                          className="material-symbols-outlined color_brand"
                          onClick={(e) => handleNotesView(row.data, e)}
                          style={{ cursor: "pointer" }}
                        >
                          chat_bubble
                        </span>
                        {!isToDo ? (
                          <span
                            className="material-icons color_brand"
                            onClick={() => onAction?.("View", row.data)}
                            style={{ cursor: "pointer" }}
                          >
                            visibility
                          </span>
                        ) : (
                          ""
                        )}
                      </div>
                    )}

                    {header.key === "dateUploaded" &&
                      (() => {
                        const [datePart, byPart] =
                          row.data.dateUploaded
                            ?.split("|")
                            .map((s: string) => s.trim()) || [];

                        return (
                          <div
                            style={{ display: "flex", flexDirection: "column" }}
                          >
                            <span style={{ fontWeight: 500 }}>{datePart}</span>

                            <OverlayTrigger
                              placement="top"
                              overlay={
                                <Tooltip id={`tooltip-by-${row.id}`}>
                                  {byPart}
                                </Tooltip>
                              }
                            >
                              <span
                                className="d-inline-block text-truncate"
                                style={{
                                  fontSize: "13px",
                                  color: "#726d6d",
                                  maxWidth: "180px",
                                  cursor: "pointer",
                                }}
                              >
                                {byPart}
                              </span>
                            </OverlayTrigger>
                          </div>
                        );
                      })()}

                    {header.key === "rate" && (
                      <div
                        style={{
                          alignItems: "end",
                          gap: "6px",
                        }}
                      >
                        <span className="amountTxt">{row.data.rate}</span>
                      </div>
                    )}

                    {header.key === "amount" && (
                      <div
                        style={{
                          alignItems: "end",
                          gap: "6px",
                        }}
                      >
                        {row.data.lineItemType === "CustomLineItem" && (
                          <OverlayTrigger
                            placement="top"
                            overlay={
                              <Tooltip id={`tooltip-custom-charge-${row.id}`}>
                                Custom Charge
                              </Tooltip>
                            }
                          >
                            <span className="customChargeBadge" />
                          </OverlayTrigger>
                        )}

                        <span className="amountTxt">{row.data.amount}</span>
                      </div>
                    )}

                    {header.key === "invoiceStatus" && (
                      <div style={{ display: "flex", flexDirection: "column" }}>
                        <span
                          className={
                            statusClassMap[row?.data?.invoiceStatus] || ""
                          }
                          style={{
                            fontWeight: "400",
                            lineHeight: "16px",
                            fontSize: "13px",
                          }}
                        >
                          {row.data.invoiceStatus}
                        </span>
                      </div>
                    )}

                    {header.key === "rateType" && (
                      <div
                        style={{
                          display: "flex",
                          alignItems: "center",
                          gap: "6px",
                        }}
                      >
                        {row.data?.billingRuleAppliedAt === "SYSTEM" && (
                          <OverlayTrigger
                            placement="top"
                            overlay={
                              <Tooltip id={`tooltip-custom-charge-${row.id}`}>
                                {row.data?.billingRuleAppliedAt_Ttp}
                              </Tooltip>
                            }
                          >
                            <span className="rateType_System">S</span>
                          </OverlayTrigger>
                        )}

                        {row.data.billingRuleAppliedAt === "TEAM" && (
                          <OverlayTrigger
                            placement="top"
                            overlay={
                              <Tooltip id={`tooltip-custom-charge-${row.id}`}>
                                {row.data?.billingRuleAppliedAt_Ttp}
                              </Tooltip>
                            }
                          >
                            <span className="rateType_Team">T</span>
                          </OverlayTrigger>
                        )}
                        {row.data.billingRuleAppliedAt === "CASE" && (
                          <OverlayTrigger
                            placement="top"
                            overlay={
                              <Tooltip id={`tooltip-custom-charge-${row.id}`}>
                                {row.data?.billingRuleAppliedAt_Ttp}
                              </Tooltip>
                            }
                          >
                            <span className="rateType_Case">C</span>
                          </OverlayTrigger>
                        )}
                        {row.data.billingRuleAppliedAt === "EXCEPTION" && (
                          <OverlayTrigger
                            placement="top"
                            overlay={
                              <Tooltip id={`tooltip-custom-charge-${row.id}`}>
                                {row.data?.billingRuleAppliedAt_Ttp}
                              </Tooltip>
                            }
                          >
                            <span
                              style={{
                                display: "inline-flex",
                                alignItems: "center",
                                justifyContent: "center",
                                width: "14px",
                                height: "14px",
                                border: "2px solid #dc3545",
                                color: "#dc3545",
                                borderRadius: "4px",
                              }}
                            >
                              <span
                                className="material-symbols-outlined"
                                style={{
                                  fontSize: "16px",
                                  lineHeight: 1,
                                }}
                              >
                                close_small
                              </span>
                            </span>
                          </OverlayTrigger>
                        )}
                        <span
                          style={{
                            fontWeight: "400",
                            lineHeight: "16px",
                            fontSize: "14px",
                            width: "75%",
                          }}
                        >
                          {row.data.rateType}
                        </span>
                      </div>
                    )}

                    {header.key !== "action" &&
                      header.key !== "dateUploaded" &&
                      header.key !== "rate" &&
                      header.key !== "amount" &&
                      header.key !== "rateType" &&
                      header.key !== "invoiceStatus" && (
                        <OverlayTrigger
                          placement="auto"
                          delay={{ show: 200, hide: 10 }}
                          overlay={
                            row.data[header.key]?.length > 15 ? (
                              <Tooltip>{row.data[header.key]}</Tooltip>
                            ) : (
                              <></>
                            )
                          }
                        >
                          <span className="truncatetwoline">
                            {row.data[header.key]}
                          </span>
                        </OverlayTrigger>
                      )}
                  </td>
                );
              }

              return null;
            })}
          </tr>
        );

        if (hasNoChildren && !row.isLoading) {
          return [
            rowElement,
            <tr key={`${row.id}-no-records`}>
              <td
                colSpan={headers.length}
                style={{
                  textAlign: "center",
                  paddingLeft: `${(row.level + 1) * 30}px`,
                }}
              >
                <span className="no_rec_data">No Record Found</span>
              </td>
            </tr>,
          ];
        }

        const childElements =
          row.isExpanded && row.children?.length
            ? renderRows(row.children)
            : [];

        return [rowElement, ...childElements];
      });
    },
    [
      headers,
      selectedRows,
      disabled,
      isLogTable,
      handleToggleExpand,
      handleParentRowSelect,
      handleLeafRowSelect,
      onAction,
      renderParentStats,
      areAllEligibleChildrenSelected,
      isSomeChildrenSelected,
      isRowEligibleForSelection,
      activeTab,
    ],
  );

  const renderedRows = useMemo(
    () => renderRows(nestedRows),
    [nestedRows, renderRows],
  );

  const isDetails = modalTitle === "Details";
  const modalProps = isDetails
    ? {
        mode: "details" as const,
        details: modalDetails,
      }
    : {
        mode: "table" as const,
        data: modalNotesData,
        headers: INVOICE_NOTES_INFO_HEADER,
      };

  return (
    <>
      <Loader isLoading={loading} />
      <div
        className="table-responsive border-0 mb-2 invoicegridss"
        style={{ maxHeight: "unset !important", display: "contents" }}
      >
        <Table className="table-hover" style={{ tableLayout: "fixed" }}>
          <thead
            className={
              isToDo
                ? "manageInvoiceStickyHeader_viewInvoice"
                : "manageInvoiceStickyHeader"
            }
          >
            <tr>
              {headers.map((header, index) => (
                <th
                  key={header.key}
                  style={{
                    border: "none",
                    padding: "8px",
                    backgroundColor: "#e0f7fa",
                    color: "#000",
                    cursor: header.sortable ? "pointer" : "default",
                    position: "sticky",
                    top: 0,
                    zIndex: 10,
                    textAlign: header.key === "action" ? "center" : "left",
                    ...header.style,
                  }}
                >
                  {index === 0 && !isToDo ? (
                    <div
                      style={{
                        display: "flex",
                        alignItems: "center",
                      }}
                    >
                      <input
                        type="checkbox"
                        ref={(el) => {
                          if (el) {
                            el.indeterminate = isSomeSelected;
                          }
                        }}
                        checked={isAllSelected}
                        onChange={handleSelectAll}
                        style={{ marginRight: "8px", cursor: "pointer" }}
                      />
                      <span>{header.label}</span>
                    </div>
                  ) : (
                    <div
                      style={{
                        display: "flex",
                        justifyContent:
                          header.key === "action" ? "center" : "space-between",
                        alignItems: "center",
                      }}
                    >
                      <span>{header.label}</span>
                    </div>
                  )}
                </th>
              ))}
            </tr>
          </thead>

          <tbody>
            {nestedRows?.length > 0 ? (
              renderedRows
            ) : (
              <tr>
                <td colSpan={headers.length} style={{ textAlign: "center" }}>
                  <span className="no_rec_data">No Record Found</span>
                </td>
              </tr>
            )}
          </tbody>
        </Table>
      </div>
      <FloatingTableModal
        show={showModal}
        onClose={() => setShowModal(false)}
        title={modalTitle}
        position={modalPos}
        {...modalProps}
      />
    </>
  );
};

export default NestedExpandableTable;
